import React, { useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  J2HGameState,
  JorneyToHomeArea,
  initialJ2HState,
  j2hReducer,
} from "./JorneyToHomeReducer";
import SocketService from "network/SocketService";
import UserSession from "network/UserSession";
import Chatbox from "components/Chatbox";
import AreaButton from "components/j2h/AreaButton";
import AreaTargetButton from "components/j2h/AreaTargetButton";
import RuleModal from "components/common/RuleModal";
import "sass/views/j2h/JorneyToHomeView.scss";
import { InGameViewProps } from "views/GamePage";
import SocketProtocol from "network/SocketProtocol";
import J2HProtocol from "views/j2h/J2HProtocol";
import ProductGameHeader from "../ProductGameHeader";

const JorneyToHomeView: React.FC<InGameViewProps> = ({
  product,
  room,
  messages,
  setMessages,
  onReturnToWaitingRoom,
}) => {
  const { t } = useTranslation();

  const [gameState, dispatch] = useReducer(j2hReducer, initialJ2HState);
  const [showMemo, setShowMemo] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winAreas, setWinAreas] = useState<number[]>([]);
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [showSkip, setShowSkip] = useState(false);

  const onClickArea = (area: JorneyToHomeArea) => {
    dispatch({ type: "SELECT_DESTINATION", selectedArea: area });
  };

  const isDisabled = (area: JorneyToHomeArea) => {
    return (
      gameState.disabledAreas.includes(area) || gameState.lastArea === area
    );
  };

  const getAreaPlayerList = (area: JorneyToHomeArea) => {
    return gameState.areaCurrentPlayerList[area];
  };

  const getAreaState = (area: JorneyToHomeArea) => {
    let areaPlayerList = getAreaPlayerList(area);
    let matchedCount = areaPlayerList.filter(
      (player) => player.home === area
    ).length;
    return [
      areaPlayerList.length.toString(),
      `${matchedCount} / ${gameState.areaHomeCountArray[area]}`,
    ];
  };

  const onClickLeave = () => {
    onReturnToWaitingRoom();
  };

  const onClickSkip = () => {
    setShowSkip(false);
    SocketService.getInstance().emit(
      SocketProtocol.SKIP_VOTE,
      product.id,
      room.roomName,
      UserSession.getInstance().getUserId()
    );
  };

  const isWinArea = (area: JorneyToHomeArea) => {
    return winAreas.includes(area);
  };

  const getPlayerWithHome = (home: JorneyToHomeArea) => {
    return gameState.playerList
      .filter((player) => player.home === home)
      .map((player) => player.name);
  };

  const getGameOverProps = (home: JorneyToHomeArea) => {
    if (!gameOver) return null;
    return { win: isWinArea(home), players: getPlayerWithHome(home) };
  };

  useEffect(() => {
    if (gameState.round > -1) {
      setShowSkip(true);
    }
  }, [gameState.currentArea, gameState.round]);

  useEffect(() => {
    if (gameState.leftTime > 0 && gameState.state === J2HGameState.START) {
      const id = setInterval(() => dispatch({ type: "DECREMENT_TIME" }), 1000);
      return () => clearInterval(id);
    }
  }, [gameState.leftTime, gameState.state]);

  useEffect(() => {
    const socket = SocketService.getInstance();

    socket.on(
      J2HProtocol.DISABLED_DESTINATIONS,
      (disabledList: JorneyToHomeArea[]) => {
        dispatch({ type: "SET_DISABLED_DESTINATIONS", disabledList });
      }
    );

    socket.on(
      J2HProtocol.ROUND_LEFT_TIME,
      ({ round, roundLeftTime, states, mode }) => {
        if (round > 0) setMessages([]);
        dispatch({
          type: "READY_ROUND",
          round,
          leftTime: roundLeftTime,
          playerStates: states,
          mode,
        });
      }
    );

    socket.on(J2HProtocol.READY_ROUND, () => {
      dispatch({
        type: "SET_DISABLED_DESTINATIONS",
        disabledList: [
          JorneyToHomeArea.Forest,
          JorneyToHomeArea.River,
          JorneyToHomeArea.Sky,
          JorneyToHomeArea.City,
        ],
      });
      socket.emit(
        J2HProtocol.READY_ROUND,
        UserSession.getInstance().user.id,
        gameState.selectedArea
      );
    });

    socket.on(
      J2HProtocol.START_ROUND,
      ({ states, roundTime, areaHomeCountArray }) => {
        dispatch({
          type: "START_ROUND",
          playerStates: states,
          roundTime,
          areaHomeCountArray,
        });
        setMessages([]);
      }
    );

    socket.on(J2HProtocol.GAME_OVER, ({ winAreas }) => {
      setShowSkip(false);
      setWinAreas(winAreas);
      setGameOver(true);
    });

    return () => {
      socket.off(J2HProtocol.DISABLED_DESTINATIONS);
      socket.off(J2HProtocol.ROUND_LEFT_TIME);
      socket.off(J2HProtocol.READY_ROUND);
      socket.off(J2HProtocol.START_ROUND);
      socket.off(J2HProtocol.GAME_OVER);
    };
  }, [gameState]);

  return (
    <div className="space-gray-theme jorney-container flex flex-col h-screen">
      <ProductGameHeader
        product={product}
        room={room}
        round={gameState.round}
        leftTime={gameState.leftTime}
        isRuleOpen={isRuleOpen}
        setIsRuleOpen={setIsRuleOpen}
        isMemoOpen={showMemo}
        setIsMemoOpen={setShowMemo}
        isSettingOpen={isSettingOpen}
        setIsSettingOpen={setIsSettingOpen}
        t={t}
      >
        {/* Leave Room button */}
        <button
          className="w-full px-4 py-2.5 bg-gray-800 hover:bg-red-600 text-red-400 hover:text-white transition-colors font-semibold flex items-center justify-center "
          onClick={() => onReturnToWaitingRoom()}
        >
          <i className="fas fa-sign-out-alt mr-2" />
          {t("leaveRoom")}
        </button>
      </ProductGameHeader>

      <div className="flex-1 flex flex-col pt-[56px] overflow-hidden">
        {/* 게임 영역 */}
        <div className="flex-shrink-0">
          <div className="relative jorney-header">
            <div className="jorney-title">{gameOver ? t("gameOver") : ""}</div>
            <button
              id="skip-button"
              className={`${
                showSkip ? "" : "hidden"
              } absolute right-0 mr-1 mt-1 px-2 py-1 rounded-lg text-sm flex items-center font-semibold transition-colors duration-150 focus:outline-none bg-yellow-500 hover:bg-yellow-600 text-white cursor-pointer
              }`}
              // disabled={isSkipVoted}
              onClick={onClickSkip}
              type="button"
            >
              {/* <i className="fa-solid fa-forward mr-2" /> */}
              {t("skip")}
            </button>
            {/* Area Buttons */}
            <div
              className={`w-full mt-4 px-2 py-1 grid gap-2 ${
                gameState.mode === 0
                  ? "grid-cols-3"
                  : gameOver
                  ? "md:grid-cols-4 grid-cols-2"
                  : "grid-cols-4"
              } ${gameState.currentArea === 0 ? "hidden" : ""}`}
            >
              {[
                JorneyToHomeArea.Sky,
                JorneyToHomeArea.Forest,
                JorneyToHomeArea.River,
                JorneyToHomeArea.City,
              ].map((area) => (
                <AreaButton
                  key={area.toString()}
                  area={area}
                  selected={gameState.selectedArea === area}
                  hidden={
                    gameState.mode === -1 ||
                    (area === JorneyToHomeArea.City && gameState.mode < 1)
                  }
                  icon={getIconForArea(area)}
                  here={gameState.currentArea === area}
                  areaState={getAreaState(area)}
                  gameOver={gameOver}
                  gameOverProps={getGameOverProps(area)}
                  lineBreakPlayerCount={gameState.mode === 1}
                />
              ))}
            </div>

            {/* Square Phase */}
            <div
              className={`w-full ${
                gameState.currentArea === 0 ? "" : "hidden"
              } `}
            >
              {gameState.round === -1 ? (
                <div className="text-white text-center text-2xl py-6">
                  {t("gameSoon")}
                </div>
              ) : (
                <>
                  <div className="flex justify-center items-center text-white">
                    <div className="text-lg mr-2">{`${t("J2H.Square")}`}</div>
                    <div className="text-sm p-1 whitespace-pre-wrap">
                      {t("J2H.squareMessage")}
                    </div>
                  </div>

                  <div
                    className={`w-full max-w-[600px] mx-auto pb-2 grid gap-2 ${
                      gameState.mode === 1 ? "grid-cols-4" : "grid-cols-3"
                    }`}
                  >
                    {[
                      JorneyToHomeArea.Sky,
                      JorneyToHomeArea.Forest,
                      JorneyToHomeArea.River,
                      JorneyToHomeArea.City,
                    ].map((area) => (
                      <AreaTargetButton
                        key={area}
                        area={area}
                        selected={gameState.selectedArea === area}
                        onClick={() => onClickArea(area)}
                        disable={isDisabled(area)}
                        hidden={
                          gameState.mode === -1 ||
                          (area === JorneyToHomeArea.City && gameState.mode < 1)
                        }
                        icon={getIconForArea(area)}
                        here={gameState.currentArea === area}
                        color={getColorForArea(area)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="left-0 top-full mt-1 w-full z-1">
              <div className="jorney-user-list">
                {!gameOver &&
                  getAreaPlayerList(gameState.currentArea).map(
                    (player, idx) => (
                      <div
                        key={idx}
                        className="flex items-center py-0.5 px-1 rounded-md outline-1 outline-offset-1 outline-indigo-700"
                      >
                        <i className="fa-solid fa-circle-user fa-sm text-green-200" />
                        <div className="ml-2 font-medium text-white flex items-center text-xs truncate">
                          {player.name}
                        </div>
                      </div>
                    )
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* 채팅창 또는 게임 종료 버튼 영역 */}
        <div className="flex-1 min-h-0">
          {gameOver ? (
            <div className="flex items-center justify-center h-full">
              <div
                className="cursor-pointer px-8 flex justify-center items-center py-2 text-center text-medium text-white outline-1 rounded-xl bg-gray-800 hover:bg-gray-700 transition-colors"
                onClick={onClickLeave}
              >
                <i className="fas fa-sign-out-alt mr-2" />
                {t("backToRoom")}
              </div>
            </div>
          ) : (
            <Chatbox
              chatList={messages}
              setChatList={setMessages}
              product={product}
              roomName={room.roomName}
              smallChatKey={JorneyToHomeArea[gameState.currentArea]}
            />
          )}
        </div>

        {showMemo && (
          <div className="absolute z-20 right-2 mt-[48px] w-[80%] md:w-[80vh] h-[50vh] bg-gray-700/95 rounded-xl p-4 shadow-2xl border border-gray-700">
            <textarea
              className="w-full h-full text-white outline-none resize-none"
              placeholder={t("enterMemo")}
            ></textarea>
          </div>
        )}
      </div>
    </div>
  );
};

export default JorneyToHomeView;

// Helper functions
const getIconForArea = (area: JorneyToHomeArea) => {
  switch (area) {
    case JorneyToHomeArea.Sky:
      return "fa-cloud";
    case JorneyToHomeArea.Forest:
      return "fa-tree";
    case JorneyToHomeArea.River:
      return "fa-droplet";
    case JorneyToHomeArea.City:
      return "fa-city";
    default:
      return "";
  }
};

const getColorForArea = (area: JorneyToHomeArea) => {
  switch (area) {
    case JorneyToHomeArea.Sky:
      return "bg-sky-500";
    case JorneyToHomeArea.Forest:
      return "bg-green-700";
    case JorneyToHomeArea.River:
      return "bg-blue-700";
    case JorneyToHomeArea.City:
      return "bg-stone-500";
    default:
      return "";
  }
};
